import React, { useState, useEffect, useMemo } from "react";
import { apiGet } from "../api";
import { formatNumberPrecise } from "../Utils";


const videoSalaryTiers = [
  {target:100000000,basic:6000,bonus:4000},{target:80000000,basic:5000,bonus:3000},
  {target:60000000,basic:4000,bonus:2000},{target:40000000,basic:3000,bonus:1000},
  {target:20000000,basic:1200,bonus:800},{target:10000000,basic:600,bonus:400},
  {target:8000000,basic:480,bonus:320},{target:6500000,basic:390,bonus:260},
  {target:4000000,basic:240,bonus:160},{target:3000000,basic:180,bonus:120},
  {target:2000000,basic:120,bonus:80},{target:1000000,basic:60,bonus:40},
  {target:500000,basic:30,bonus:20},{target:300000,basic:16.78,bonus:11},
];
const audioSalaryTiers = [
  {target:100000000,basic:6000,bonus:2000},{target:80000000,basic:5000,bonus:1400},
  {target:60000000,basic:4000,bonus:800},{target:40000000,basic:2600,bonus:600},
  {target:20000000,basic:1200,bonus:400},{target:10000000,basic:600,bonus:200},
  {target:8000000,basic:480,bonus:160},{target:6500000,basic:390,bonus:130},
  {target:4000000,basic:240,bonus:80},{target:3000000,basic:180,bonus:60},
  {target:2000000,basic:120,bonus:30},{target:1000000,basic:65,bonus:15},
  {target:500000,basic:30,bonus:10},{target:300000,basic:16,bonus:8},
];
const agencyCommissionTiers = [
  {target:100000000,commission:1500},{target:80000000,commission:1200},
  {target:60000000,commission:900},{target:40000000,commission:600},
  {target:20000000,commission:300},{target:10000000,commission:150},
  {target:8000000,commission:120},{target:6500000,commission:97.5},
  {target:4000000,commission:60},{target:3000000,commission:45},
  {target:2000000,commission:30},{target:1000000,commission:15},
  {target:500000,commission:7.50},{target:300000,commission:2.22},
];

function calculateHostSalary(gold, liveDays, liveHours, hostType) {
  const tiers = hostType === "video" ? videoSalaryTiers : audioSalaryTiers;
  let basicSalary = 0, timeBonus = 0;
  for (const tier of tiers) {
    if (gold >= tier.target) {
      basicSalary = tier.basic;
      if (liveDays >= 12 && liveHours >= 16) timeBonus = tier.bonus;
      break;
    }
  }
  return { basicSalary, timeBonus, totalSalary: basicSalary + timeBonus };
}

function calculateAgencyCommission(gold) {
  for (const tier of agencyCommissionTiers) { if (gold >= tier.target) return tier.commission; }
  return 0;
}

const SalarySheet = () => {
  const [archives, setArchives] = useState([]);
  const [hostData, setHostData] = useState([]);
  const [liveHosts, setLiveHosts] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agency, setAgency] = useState(null);
  const [focusedRow, setFocusedRow] = useState(null);

  // Load this agency's own profile + live hosts (with monthlyStats) + past cycle
  // archives from the backend, all at once. (Was Firestore agencies/{uid} +
  // a paginated users query + agencies/{uid}/archives subcollection.)
  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const { profile } = await apiGet('/api/account/me');
        if (!profile) throw new Error('Agency profile not found.');
        if (!alive) return;
        setAgency(profile);
        const [hosts, arch] = await Promise.all([
          apiGet(`/api/salary/hosts?agencyIds=${profile.ID}`),
          apiGet(`/api/salary/archives?agencyIds=${profile.ID}`),
        ]);
        if (!alive) return;
        setLiveHosts((Array.isArray(hosts) ? hosts : []).sort((a, b) => (b.monthlyStats?.totalGold || 0) - (a.monthlyStats?.totalGold || 0)));
        setArchives((Array.isArray(arch) ? arch : []).map(a => ({ id: a.cycleId, data: a })));
      } catch (err) { if (alive) setError(err.message || 'Failed to load salary data.'); }
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setFocusedRow(null);
    if (selectedPeriod === "current") {
      setHostData(liveHosts);
    } else {
      const found = archives.find(a => a.id === selectedPeriod);
      setHostData(found ? [...(found.data.hostRecords || [])].sort((a, b) => (b.Gold || 0) - (a.Gold || 0)) : []);
    }
  }, [selectedPeriod, liveHosts, archives]);

  const isLiveMode = selectedPeriod === "current";
  const selectedArchiveData = !isLiveMode ? archives.find(a => a.id === selectedPeriod)?.data : null;

  const liveTotals = useMemo(() => {
    if (!isLiveMode) return { totalGold: 0, totalSalary: 0, totalCommission: 0 };
    return hostData.reduce((acc, r) => {
      const stats = r.monthlyStats || {};
      const gold = stats.totalGold || 0;
      const sal = calculateHostSalary(gold, stats.validLiveDays || 0, stats.totalLiveHours || 0, r.hostType || "audio");
      acc.totalGold += gold; acc.totalSalary += sal.totalSalary; acc.totalCommission += calculateAgencyCommission(gold);
      return acc;
    }, { totalGold: 0, totalSalary: 0, totalCommission: 0 });
  }, [hostData, isLiveMode]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-10 h-10 rounded-full border-4 border-white/10 border-l-[#ff4d6d] animate-spin" />
      <p className="text-sm text-white/40">Loading Salary Dashboard...</p>
    </div>
  );

  if (error) return (
    <div className="p-4">
      <div className="rounded-xl p-4 text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#f87171' }}>{error}</div>
    </div>
  );

  const summaryCards = [
    // Was `showAll ? '(All)' : '(Loaded)'` from when hosts were paged in.
    // /api/salary/hosts returns the agency's full list in one call, so the live
    // figure is always the complete one — and `showAll` no longer exists.
    { label: `Total Gold ${isLiveMode ? '(All)' : '(Final)'}`, value: formatNumberPrecise(isLiveMode ? liveTotals.totalGold : (selectedArchiveData?.totalGold || 0)), color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: 'fa-coins' },
    { label: `Total Host Salary ${isLiveMode ? '' : '(Final)'}`, value: `$${(isLiveMode ? liveTotals.totalSalary : (selectedArchiveData?.totalSalary || 0)).toFixed(2)}`, color: '#4ade80', bg: 'rgba(74,222,128,0.12)', icon: 'fa-money-bill-wave' },
    { label: `Agency Commission ${isLiveMode ? '' : '(Final)'}`, value: `$${(isLiveMode ? liveTotals.totalCommission : (selectedArchiveData?.totalCommission || 0)).toFixed(2)}`, color: '#c084fc', bg: 'rgba(192,132,252,0.12)', icon: 'fa-percentage' },
  ];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-white">Salary Dashboard</h1>
          {agency && <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Agency: <span style={{ color: '#ff4d6d', fontWeight: 600 }}>{agency.name}</span></p>}
        </div>
        {/* Period selector */}
        <div className="relative">
          <select
            value={selectedPeriod}
            onChange={e => setSelectedPeriod(e.target.value)}
            className="appearance-none rounded-xl pl-4 pr-10 py-2.5 text-sm font-semibold text-white outline-none cursor-pointer"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            <option value="current" style={{ background: '#13132a' }}>Current Month (Live)</option>
            {archives.map(a => <option key={a.id} value={a.id} style={{ background: '#13132a' }}>Report - {a.id}</option>)}
          </select>
          <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-xs pointer-events-none" style={{ color: 'rgba(255,255,255,0.4)' }} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        {summaryCards.map((card, i) => (
          <div key={i} className="rounded-2xl p-4 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: card.bg }}>
              <i className={`fas ${card.icon} text-sm`} style={{ color: card.color }} />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider truncate" style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{card.label}</p>
              <p className="font-extrabold text-lg text-white">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Table section */}
      <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <p className="font-bold text-white text-sm">{isLiveMode ? 'Live Performance' : `Report: ${selectedPeriod}`}</p>
            {selectedArchiveData && selectedArchiveData.reportDate && <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Generated: {new Date(selectedArchiveData.reportDate).toLocaleDateString()}</p>}
          </div>
          <span className="text-xs font-bold rounded-full px-3 py-1" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
            {hostData.length} hosts loaded
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 800, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['#', 'Host Name', 'Host ID', 'Gold', 'Days', 'Hrs', 'Basic ($)', 'Bonus ($)', 'Salary ($)', 'Comm. ($)'].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap', textAlign: i >= 3 ? 'right' : 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hostData.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-12 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>No host data available for this period.</td></tr>
              ) : hostData.map((record, index) => {
                const isArchived = !isLiveMode;
                const stats = record.monthlyStats || {};
                const gold = isArchived ? (record.Gold ?? 0) : (stats.totalGold ?? 0);
                const days = isArchived ? (record.days ?? 0) : (stats.validLiveDays ?? 0);
                const hours = isArchived ? (record.hours ?? 0) : (stats.totalLiveHours ?? 0);
                const sal = calculateHostSalary(gold, days, hours, record.hostType || "audio");
                const basic = isArchived ? (record.basicSalary ?? 0) : sal.basicSalary;
                const bonus = isArchived ? (record.timeBonus ?? 0) : sal.timeBonus;
                const total = isArchived ? (record.totalSalary ?? 0) : sal.totalSalary;
                const comm = isArchived ? (record.agencyCommission ?? calculateAgencyCommission(gold)) : calculateAgencyCommission(gold);
                const rowId = isLiveMode ? record.id : record.userId;
                const isFocused = focusedRow === rowId;
                return (
                  <tr
                    key={`${rowId}-${index}`}
                    onClick={() => setFocusedRow(isFocused ? null : rowId)}
                    className="cursor-pointer transition-colors"
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: isFocused ? 'rgba(255,77,109,0.07)' : 'transparent' }}
                    onMouseEnter={e => { if (!isFocused) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={e => { if (!isFocused) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-4 py-3 text-right" style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>{index + 1}</td>
                    <td className="px-4 py-3 font-semibold text-white">{record.username}</td>
                    <td className="px-4 py-3 font-mono text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{isLiveMode ? record.ID : record.userId}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-xs rounded-lg px-2 py-1" style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24' }}>{formatNumberPrecise(gold)}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{days}</td>
                    <td className="px-4 py-3 text-right text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>{typeof hours === 'number' ? hours.toFixed(1) : hours}</td>
                    <td className="px-4 py-3 text-right text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>${basic.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>${bonus.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-xs rounded-lg px-2 py-1" style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80' }}>${total.toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-sm" style={{ color: '#c084fc' }}>${comm.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default SalarySheet;
